import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ConfigProvider, DatePicker, theme as antTheme } from "antd";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { SwapOutlined } from "@ant-design/icons";
import { useTheme } from "../context/ThemeContext";
import DroughtTrendChart from "../components/DroughtComponents/DroughtTrendData";
import {
  Plus,
  Minus,
  Droplet,
  AlertTriangle,
  Activity,
  MapPin,
  Eye,
  // CalendarDays,
  // ArrowLeftRight,
  Download,
} from "lucide-react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClustererF,
} from "@react-google-maps/api";
import {
  getDroughtSummary,
  getDroughtTopOffenders,
  getDroughtViolationsByDay,
  getDroughtViolationsByHour,
  getDroughtRateCodeSplit,
  getDroughtIrrigationImpact,
  getDroughtRecentEvents,
  getDroughtMapLocations,
   getDroughtTrendAnalysis,
} from "../services";

const violationPoints = [
  { top: "24%", left: "60%" },
  { top: "36%", left: "45%" },
  { top: "43%", left: "68%" },
  { top: "56%", left: "52%" },
  { top: "73%", left: "38%" },
  { top: "81%", left: "55%" },
];

const heatPoints = [
  { top: "22%", left: "51%", size: "w-28 h-28" },
  { top: "28%", left: "57%", size: "w-24 h-24" },
  { top: "67%", left: "62%", size: "w-28 h-20" },
  { top: "70%", left: "54%", size: "w-20 h-20" },
];

const { RangePicker } = DatePicker;

function StatCard({ title, value, unit, change, changeColor = "green" }) {
  return (
    <div className="rounded-lg border border-slate-700/70 bg-[#162a41] px-5 py-5 shadow-lg">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-sky-300/80">
            {title}
          </p>

          <div className="mt-2 flex items-end gap-2">
            <h2 className="text-3xl font-extrabold text-white">{value}</h2>
            {unit && (
              <span className="mb-1 text-xs italic text-slate-400">{unit}</span>
            )}
          </div>

          {/* <p className="mt-1 text-xs text-slate-500">vs. previous period</p> */}
        </div>

        {change && (
          <span
            className={`rounded px-2 py-1 text-xs font-bold ${
              changeColor === "green"
                ? "bg-emerald-500/15 text-emerald-400"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {change}
          </span>
        )}
      </div>
    </div>
  );
}

const chipToRange = (chip, today = dayjs()) => {
  const end = dayjs(today);
  let start = dayjs(today);

  switch (chip) {
    case "D":
      start = end.subtract(1, "day");
      break;
    case "W":
      start = end.subtract(7, "day");
      break;
    case "1M":
      start = end.subtract(1, "month");
      break;
    case "3M":
      start = end.subtract(3, "month");
      break;
    case "6M":
      start = end.subtract(6, "month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    case "1Y":
      start = end.subtract(1, "year");
      break;
    default:
      break;
  }

  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  };
};

// const { isLoaded } = useJsApiLoader({
//   id: "google-map-script",
//   googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
// });

// const [map, setMap] = useState(null);

// const onMapLoad = (mapInstance) => {
//   setMap(mapInstance);
// };

const mapContainerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: "8px",
};

const mapCenter = {
  lat: 29.92312,
  lng: -95.20392,
};

const MAP_EVENT_COLORS = {
  warning: {
    dot: "#f59e0b",
    soft: "rgba(245,158,11,0.25)",
    mid: "rgba(245,158,11,0.5)",
    text: "#f59e0b",
  },
  violation: {
    dot: "#ef4444",
    soft: "rgba(239,68,68,0.25)",
    mid: "rgba(239,68,68,0.5)",
    text: "#ef4444",
  },
};

const DroughtDashboard = () => {
  const { colors, theme } = useTheme();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);

  const markersRef = useRef([]);
  const clustererRef = useRef(null);

  const onMapLoad = (mapInstance) => {
    setMap(mapInstance);
  };
  const [selectedRange, setSelectedRange] = useState("1M");
  const [selectedInterval, setSelectedInterval] = useState("Day");

  const [dateRange, setDateRange] = useState(() => chipToRange("1M"));
  const [summaryData, setSummaryData] = useState({
    total_warnings: 0,
    total_violations: 0,
    violation_water: 0,
  });

  const [loadingSummary, setLoadingSummary] = useState(false);

  const [topOffenders, setTopOffenders] = useState([]);
  const [loadingTopOffenders, setLoadingTopOffenders] = useState(false);

  const [violationsByDay, setViolationsByDay] = useState([]);
  const [loadingViolationsByDay, setLoadingViolationsByDay] = useState(false);
  const [dayTooltip, setDayTooltip] = useState(null);

  const [violationsByHour, setViolationsByHour] = useState([]);
  const [loadingViolationsByHour, setLoadingViolationsByHour] = useState(false);

  const [rateCodeSplit, setRateCodeSplit] = useState([]);
  const [loadingRateCodeSplit, setLoadingRateCodeSplit] = useState(false);
  // const [rateCodeTooltip, setRateCodeTooltip] = useState(null);

  const [irrigationImpact, setIrrigationImpact] = useState({
    impact_percent: 0,
    total_water: 0,
    violation_water: 0,
  });

  const [loadingIrrigationImpact, setLoadingIrrigationImpact] = useState(false);

  const [recentEvents, setRecentEvents] = useState([]);
  const [loadingRecentEvents, setLoadingRecentEvents] = useState(false);

  const [mapLocations, setMapLocations] = useState([]);
  const [loadingMapLocations, setLoadingMapLocations] = useState(false);

  const ranges = ["D", "W", "1M", "3M", "6M", "YTD", "1Y"];
  const intervals = ["Hour", "Day", "Week"];

  //  const topTwoPeakDays = [...violationsByDay]
  //   .sort((a, b) => b.count - a.count)
  //   .slice(0, 2)
  //   .filter((item) => Number(item.count || 0) > 0)
  //   .map((item) => item.day);

  const isLightTheme = theme === "white" || theme === "light";

  const filterCardStyle = {
    backgroundColor: colors.cardBg,
    borderColor: colors.border,
  };

  const controlStyle = {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.text,
  };

  const activeButtonStyle = {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: colors.buttonText || "#ffffff",
  };

  const inactiveButtonStyle = {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.textSecondary,
  };

  useEffect(() => {
    if (!map || !isLoaded || !clustererRef.current || !window.google) return;

    markersRef.current.forEach((marker) => marker && marker.setMap(null));
    clustererRef.current.clearMarkers(true);
    markersRef.current = [];

    if (mapLocations.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();

    const markers = mapLocations.map((item) => {
      const violationCount = Number(item.violationCount || 0);
      const warningCount = Number(item.warningCount || 0);

      const hasViolation = violationCount > 0;
      const markerColor = hasViolation
        ? MAP_EVENT_COLORS.violation
        : MAP_EVENT_COLORS.warning;

      const marker = new window.google.maps.Marker({
        position: {
          lat: item.lat,
          lng: item.lng,
        },
        icon: {
          url:
            "data:image/svg+xml;base64," +
            btoa(`
      <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="32" r="28" fill="${markerColor.soft}" />
        <circle cx="32" cy="32" r="15" fill="${markerColor.mid}" />
        <circle cx="32" cy="32" r="8" fill="${markerColor.dot}" />
        <circle cx="32" cy="32" r="4" fill="#ffffff" />
      </svg>
    `),
          scaledSize: new window.google.maps.Size(42, 42),
          anchor: new window.google.maps.Point(21, 21),
        },
        title: `${item.address} - ${warningCount} warning(s), ${violationCount} violation(s)`,
      });

      marker.addListener("click", () => {
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
          <div style="min-width:220px">
            <div style="font-weight:700;margin-bottom:6px;color:#111827">
              ${item.address}
            </div>
            <div style="font-size:12px;color:#374151">
              Location ID: ${item.id || "-"}
            </div>
            <div style="font-size:12px;margin-top:8px;color:#f59e0b;font-weight:700">
              Warnings: ${warningCount}
            </div>

            <div style="font-size:12px;margin-top:4px;color:#ef4444;font-weight:700">
              Violations: ${violationCount}
            </div>
          </div>
        `,
        });

        infoWindow.open(map, marker);
      });

      bounds.extend({
        lat: item.lat,
        lng: item.lng,
      });

      return marker;
    });

    markersRef.current = markers;
    clustererRef.current.addMarkers(markers);

    if (markers.length === 1) {
      map.setCenter({
        lat: mapLocations[0].lat,
        lng: mapLocations[0].lng,
      });
      map.setZoom(14);
    } else {
      map.fitBounds(bounds);
    }
  }, [map, isLoaded, mapLocations]);

  const formatWaterWaste = (value) => {
    const num = Number(value || 0);

    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }

    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }

    return num.toLocaleString("en-US");
  };

  // kpi data fetching simulation
  useEffect(() => {
    const fetchDroughtSummary = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingSummary(true);

        const res = await getDroughtSummary({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        const apiData = res?.data || {};

        setSummaryData({
          total_warnings: apiData.total_warnings || 0,
          total_violations: apiData.total_violations || 0,
          violation_water: apiData.violation_water || 0,
        });
      } catch (error) {
        console.error(
          "Drought summary API error:",
          error.response?.data || error.message,
        );

        setSummaryData({
          total_warnings: 0,
          total_violations: 0,
          violation_water: 0,
        });
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchDroughtSummary();
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    const fetchTopOffenders = async () => {
      try {
        setLoadingTopOffenders(true);

        const res = await getDroughtTopOffenders();
        const apiData = Array.isArray(res?.data) ? res.data : [];

        setTopOffenders(apiData);
      } catch (error) {
        console.error(
          "Drought top offenders API error:",
          error.response?.data || error.message,
        );

        setTopOffenders([]);
      } finally {
        setLoadingTopOffenders(false);
      }
    };

    fetchTopOffenders();
  }, []);

  const formatOffenseDate = (value) => {
    if (!value) return "-";

    const date = dayjs(value);

    return date.isValid() ? date.format("MMM DD, YYYY") : "-";
  };

  //

  const getNormalizedViolationsByDay = (apiData = []) => {
    const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const countMap = new Map(
      apiData.map((item) => [
        String(item.day || "").toLowerCase(),
        Number(item.count || 0),
      ]),
    );

    const maxCount = Math.max(
      ...dayOrder.map((day) => countMap.get(day.toLowerCase()) || 0),
      1,
    );

    return dayOrder.map((day) => {
      const count = countMap.get(day.toLowerCase()) || 0;

      return {
        day: day.toUpperCase(),
        count,
        value: count > 0 ? Math.max((count / maxCount) * 100, 8) : 0,
      };
    });
  };

  useEffect(() => {
    const fetchViolationsByDay = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingViolationsByDay(true);

        const res = await getDroughtViolationsByDay({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        const apiData = Array.isArray(res?.data) ? res.data : [];

        setViolationsByDay(getNormalizedViolationsByDay(apiData));
      } catch (error) {
        console.error(
          "Drought violations by day API error:",
          error.response?.data || error.message,
        );

        setViolationsByDay(getNormalizedViolationsByDay([]));
      } finally {
        setLoadingViolationsByDay(false);
      }
    };

    fetchViolationsByDay();
  }, [dateRange.start, dateRange.end]);

  //
  // hourly violations fetching

  const formatHourLabel = (hourValue) => {
    const hour = Number(hourValue || 0);

    const start = dayjs().hour(hour).minute(0).second(0).format("hh:00 A");

    const end = dayjs()
      .hour(hour + 1)
      .minute(0)
      .second(0)
      .format("hh:00 A");

    return `${start} - ${end}`;
  };

  const getNormalizedViolationsByHour = (apiData = []) => {
    const maxCount = Math.max(
      ...apiData.map((item) => Number(item.count || 0)),
      1,
    );

    return apiData
      .map((item) => {
        const count = Number(item.count || 0);
        const hour = Number(item.hour || 0);

        return {
          hour,
          count,
          label: formatHourLabel(hour),
          value: count > 0 ? Math.max((count / maxCount) * 100, 8) : 0,
        };
      })
      .sort((a, b) => a.hour - b.hour);
  };

  useEffect(() => {
    const fetchViolationsByHour = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingViolationsByHour(true);

        const res = await getDroughtViolationsByHour({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        const apiData = Array.isArray(res?.data) ? res.data : [];

        setViolationsByHour(getNormalizedViolationsByHour(apiData));
      } catch (error) {
        console.error(
          "Drought violations by hour API error:",
          error.response?.data || error.message,
        );

        setViolationsByHour([]);
      } finally {
        setLoadingViolationsByHour(false);
      }
    };

    fetchViolationsByHour();
  }, [dateRange.start, dateRange.end]);

  // Rate Code Split card

  const getRateCodeColor = (rateCode, index) => {
    const name = String(rateCode || "").toLowerCase();

    if (name.includes("potable domestic")) return "#0284c7";
    if (name.includes("non-potable")) return "#9333ea";

    const fallbackColors = ["#0ea5e9", "#9333ea", "#22c55e", "#f97316"];
    return fallbackColors[index % fallbackColors.length];
  };

  const rateCodeChartData = rateCodeSplit.map((item, index) => ({
    name: item.rate_code || "-",
    value: Number(item.percentage || 0),
    count: Number(item.count || 0),
    percentage: Number(item.percentage || 0),
    color: getRateCodeColor(item.rate_code, index),
  }));

  const mainRateCode = rateCodeChartData?.[0];

  const RateCodeTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0]?.payload;

    return (
      <div
        className="rounded-lg border px-3 py-2 text-xs shadow-xl"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          color: colors.text,
        }}
      >
        <p className="mb-2 font-bold" style={{ color: colors.primary }}>
          Rate Code Split
        </p>

        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: item.color }}
            />

            <span className="truncate" style={{ color: colors.textSecondary }}>
              {item.name}
            </span>
          </div>

          <span className="shrink-0 font-bold" style={{ color: colors.text }}>
            {item.count} / {item.percentage.toFixed(0)}%
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchRateCodeSplit = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingRateCodeSplit(true);

        const res = await getDroughtRateCodeSplit({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        const apiData = Array.isArray(res?.data) ? res.data : [];

        setRateCodeSplit(apiData);
      } catch (error) {
        console.error(
          "Drought rate code split API error:",
          error.response?.data || error.message,
        );

        setRateCodeSplit([]);
      } finally {
        setLoadingRateCodeSplit(false);
      }
    };

    fetchRateCodeSplit();
  }, [dateRange.start, dateRange.end]);

  // Irrigation Impact

  const formatGallons = (value) => {
    const num = Number(value || 0);

    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    }

    if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }

    return num.toLocaleString("en-US");
  };

  // const getImpactPercent = (data) => {
  //   const apiPercent = Number(data?.impact_percent || 0);
  //   const totalWater = Number(data?.total_water || 0);
  //   const violationWater = Number(data?.violation_water || 0);

  //   // Use backend percentage only if backend sends valid value
  //   if (apiPercent > 0) return apiPercent;

  //   // Otherwise calculate from API water values
  //   if (totalWater > 0 && violationWater > 0) {
  //     return (violationWater / totalWater) * 100;
  //   }

  //   return 0;
  // };

  const irrigationImpactPercent = Number(irrigationImpact?.impact_percent || 0);

  const displayIrrigationImpact = `${irrigationImpactPercent.toFixed(4)}%`;

  // For chart visibility only. Actual value is still shown correctly in center.
  const hasViolationWater = Number(irrigationImpact?.violation_water || 0) > 0;

  // This is only for graph visibility.
  // Percentage text still comes only from backend impact_percent.
  const chartImpactPercent =
    irrigationImpactPercent > 0
      ? irrigationImpactPercent < 1
        ? 1
        : Math.min(Math.max(irrigationImpactPercent, 0), 100)
      : hasViolationWater
        ? 1
        : 0;

  const irrigationImpactChartData = [
    {
      name: "Violation Water",
      value: chartImpactPercent,
      actualPercent: irrigationImpactPercent,
      water: Number(irrigationImpact.violation_water || 0),
      color: "#ff4b55",
    },
    {
      name: "Remaining Water",
      value: Math.max(100 - chartImpactPercent, 0),
      actualPercent: 0,
      water: Math.max(
        Number(irrigationImpact.total_water || 0) -
          Number(irrigationImpact.violation_water || 0),
        0,
      ),
      color: "#1e293b",
    },
  ];

  const IrrigationImpactTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0]?.payload;

    return (
      <div
        className="min-w-[190px] rounded-lg border px-3 py-3 text-xs shadow-xl"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          color: colors.text,
        }}
      >
        <p className="mb-3 font-bold" style={{ color: colors.primary }}>
          Irrigation Impact
        </p>

        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <span
              className="mt-1 h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: item.color }}
            />

            <span
              className="leading-snug"
              style={{ color: colors.textSecondary }}
            >
              {item.name}
            </span>
          </div>

          <span
            className="shrink-0 whitespace-nowrap text-right font-bold"
            style={{ color: colors.text }}
          >
            {formatGallons(item.water)} GAL
          </span>
        </div>

        <div
          className="mt-3 flex items-center justify-between gap-3 border-t pt-2"
          style={{ borderColor: colors.border }}
        >
          <span style={{ color: colors.textSecondary }}>Impact</span>

          <span
            className="shrink-0 whitespace-nowrap font-bold"
            style={{ color: colors.text }}
          >
            {item.name === "Violation Water" ? displayIrrigationImpact : "-"}
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchIrrigationImpact = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingIrrigationImpact(true);

        const res = await getDroughtIrrigationImpact({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        const apiData = res?.data || {};

        setIrrigationImpact({
          impact_percent: Number(apiData.impact_percent || 0),
          total_water: Number(apiData.total_water || 0),
          violation_water: Number(apiData.violation_water || 0),
        });
      } catch (error) {
        console.error(
          "Drought irrigation impact API error:",
          error.response?.data || error.message,
        );

        setIrrigationImpact({
          impact_percent: 0,
          total_water: 0,
          violation_water: 0,
        });
      } finally {
        setLoadingIrrigationImpact(false);
      }
    };

    fetchIrrigationImpact();
  }, [dateRange.start, dateRange.end]);

  // return events fetching

  const formatDuration = (hours) => {
    const value = Number(hours || 0);

    if (value <= 0) return "-";
    if (value === 1) return "1 hr";

    return `${value} hrs`;
  };

  const formatAvgFlow = (value) => {
    const num = Number(value || 0);

    if (!num) return "0 GPH";

    return `${num.toLocaleString("en-US")} GPH`;
  };

  const formatEventType = (value) => {
    const type = String(value || "").toLowerCase();

    if (type === "violation") return "Violation";
    if (type === "warning") return "Warning";

    return "-";
  };

  useEffect(() => {
    const fetchRecentEvents = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingRecentEvents(true);

        const res = await getDroughtRecentEvents({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        console.log("Recent Events API Response:", res);

        const apiData = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];

        const formattedEvents = apiData.map((item) => ({
          id: item.violation_id,
          address: item.address || "-",
          meterId: item.meter_id || "-",
          eventTime: item.violation_time || "-",
          duration: formatDuration(item.duration_hours),
          avgFlow: formatAvgFlow(item.avg_flow_gph),
          type: formatEventType(item.violation_type),
        }));

        setRecentEvents(formattedEvents);
      } catch (error) {
        console.error(
          "Drought recent events API error:",
          error.response?.data || error.message,
        );

        setRecentEvents([]);
      } finally {
        setLoadingRecentEvents(false);
      }
    };

    fetchRecentEvents();
  }, [dateRange.start, dateRange.end]);

  const downloadRecentEventsCSV = () => {
    if (!recentEvents.length) return;

    const headers = [
      "Address",
      "Meter ID",
      "Event Time",
      "Duration",
      "Avg Flow",
      "Type",
    ];

    const rows = recentEvents.map((item) => [
      item.address,
      item.meterId,
      item.eventTime,
      item.duration,
      item.avgFlow,
      item.type,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `recent-events-${dateRange.start}-to-${dateRange.end}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  // Locations fetching for map

  useEffect(() => {
    const fetchMapLocations = async () => {
      if (!dateRange.start || !dateRange.end) return;

      try {
        setLoadingMapLocations(true);

        const res = await getDroughtMapLocations({
          from_date: dateRange.start,
          to_date: dateRange.end,
        });

        console.log("Drought Map Locations API Response:", res);

        const apiData = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];

        const formattedLocations = apiData
          .map((item) => ({
            id: item.location_id,
            lat: Number(item.latitude),
            lng: Number(item.longitude),
            address: item.address || "-",

            warningCount: Number(
              item.warning_count ?? item.warningCount ?? item.warnings ?? 0,
            ),

            violationCount: Number(
              item.violation_count ??
                item.violationCount ??
                item.violations ??
                0,
            ),
          }))
          .filter(
            (item) => Number.isFinite(item.lat) && Number.isFinite(item.lng),
          );

        setMapLocations(formattedLocations);
      } catch (error) {
        console.error(
          "Drought map locations API error:",
          error.response?.data || error.message,
        );

        setMapLocations([]);
      } finally {
        setLoadingMapLocations(false);
      }
    };

    fetchMapLocations();
  }, [dateRange.start, dateRange.end]);

  const activeWarningCount = mapLocations.reduce(
    (total, item) => total + Number(item.warningCount || 0),
    0,
  );

  const activeViolationCount = mapLocations.reduce(
    (total, item) => total + Number(item.violationCount || 0),
    0,
  );

  const currentMapCenter =
    mapLocations.length > 0
      ? {
          lat: mapLocations[0].lat,
          lng: mapLocations[0].lng,
        }
      : mapCenter;

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1
              className="bg-clip-text text-4xl font-bold text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.primary}, ${
                  colors.secondary || colors.primary
                })`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: "normal",
              }}
            >
              Drought Control Dashboard
            </h1>
          </div>

          <p className="mt-2" style={{ color: colors.textSecondary }}>
            District-wide monitoring of water usage, violations, warnings, and
            drought control insights
          </p>
        </div>
      </div>
      {/* Filter Bar */}
      {/* Filter Bar */}
      <div
        className="mb-6 rounded-b-2xl border px-4 py-4 shadow-xl"
        style={filterCardStyle}
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          {/* Left Range Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {ranges.map((range) => {
              const isActive = selectedRange === range;

              return (
                <button
                  key={range}
                  type="button"
                  onClick={() => {
                    const nextRange = chipToRange(range);

                    setSelectedRange(range);
                    setDateRange(nextRange);
                  }}
                  className="min-w-[48px] rounded-lg border px-4 py-2 text-sm font-medium transition-all"
                  style={isActive ? activeButtonStyle : inactiveButtonStyle}
                >
                  {range}
                </button>
              );
            })}
          </div>

          {/* Right Controls */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
            {/* Date Range */}
            <div
              className="flex h-[40px] items-center rounded-lg border px-2"
              style={controlStyle}
            >
              <ConfigProvider
                theme={{
                  algorithm: isLightTheme
                    ? antTheme.defaultAlgorithm
                    : antTheme.darkAlgorithm,
                  token: {
                    colorPrimary: colors.primary,
                    colorBgContainer: colors.background,
                    colorText: colors.text,
                    colorTextPlaceholder: colors.textSecondary,
                    colorBorder: "transparent",
                    controlHeight: 36,
                    borderRadius: 8,
                  },
                }}
              >
                <RangePicker
                  value={[
                    dateRange.start
                      ? dayjs(dateRange.start, "YYYY-MM-DD")
                      : null,
                    dateRange.end ? dayjs(dateRange.end, "YYYY-MM-DD") : null,
                  ]}
                  onChange={(dates, dateStrings) => {
                    const [start, end] = dateStrings;

                    if (start && end) {
                      setDateRange({
                        start,
                        end,
                      });

                      setSelectedRange(null);
                    }
                  }}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  placeholder={["Start", "End"]}
                  variant="borderless"
                  separator={
                    <SwapOutlined style={{ color: colors.textSecondary }} />
                  }
                  className="custom-range-picker cursor-pointer"
                  style={{
                    width: "240px",
                    background: "transparent",
                    color: colors.text,
                  }}
                  classNames={{
                    popup: {
                      root: "mizu-calendar-popup",
                    },
                  }}
                />
              </ConfigProvider>
            </div>

            {/* Interval Buttons */}
            {/* <div
              className="flex overflow-hidden rounded-lg border"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {intervals.map((interval) => {
                const isActive = selectedInterval === interval;

                return (
                  <button
                    key={interval}
                    type="button"
                    onClick={() => setSelectedInterval(interval)}
                    className="px-5 py-2 text-sm font-medium transition-all"
                    style={{
                      backgroundColor: isActive
                        ? colors.primary
                        : "transparent",
                      color: isActive
                        ? colors.buttonText || "#ffffff"
                        : colors.textSecondary,
                    }}
                  >
                    {interval}
                  </button>
                );
              })}
            </div> */}

            {/* Clear Button */}
            {/* <button
              type="button"
              onClick={() => {
                const defaultRange = chipToRange("1M");

                setSelectedRange("1M");
                setSelectedInterval("Day");
                setDateRange(defaultRange);
              }}
              className="rounded-lg border px-5 py-2 text-sm font-medium transition-all"
              style={inactiveButtonStyle}
            >
              Clear
            </button>

            <button
              type="button"
              className="rounded-lg border px-6 py-2 text-sm font-bold transition-all"
              style={activeButtonStyle}
            >
              Apply
            </button> */}
          </div>
        </div>
      </div>

      {/* Top Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:w-[75%]">
        <StatCard
          title="Total Warnings"
          value={
            loadingSummary
              ? "..."
              : Number(summaryData.total_warnings || 0).toLocaleString("en-US")
          }
          change=""
          changeColor="green"
        />

        <StatCard
          title="Total Violations"
          value={
            loadingSummary
              ? "..."
              : Number(summaryData.total_violations || 0).toLocaleString(
                  "en-US",
                )
          }
          change=""
          changeColor="red"
        />

        <StatCard
          title="Estimated Violation Water Usage"
          value={
            loadingSummary
              ? "..."
              : formatWaterWaste(summaryData.violation_water)
          }
          unit="GAL"
          change=""
          changeColor="red"
        />
      </div>

      {/* Main Section */}
      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1fr_380px]">
        {/* Live Google Map Card */}
        <div
          className="relative h-[500px] overflow-hidden rounded-lg border shadow-xl"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          {!isLoaded || loadingMapLocations ? (
            <div className="flex h-full items-center justify-center">
              <span style={{ color: colors.textSecondary }}>
                Loading map...
              </span>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={currentMapCenter}
              zoom={13}
              onLoad={onMapLoad}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                gestureHandling: "greedy",
                styles: [
                  {
                    elementType: "geometry",
                    stylers: [{ color: "#0b1f33" }],
                  },
                  {
                    elementType: "labels.text.fill",
                    stylers: [{ color: "#8ba1c7" }],
                  },
                  {
                    elementType: "labels.text.stroke",
                    stylers: [{ color: "#061727" }],
                  },
                  {
                    featureType: "road",
                    elementType: "geometry",
                    stylers: [{ color: "#1e3a5f" }],
                  },
                  {
                    featureType: "water",
                    elementType: "geometry",
                    stylers: [{ color: "#08243a" }],
                  },
                  {
                    featureType: "poi",
                    stylers: [{ visibility: "off" }],
                  },
                ],
              }}
            >
              <MarkerClustererF
                onLoad={(clusterer) => {
                  clustererRef.current = clusterer;
                }}
                options={{
                  maxZoom: 18,
                  gridSize: 40,
                }}
              >
                {() => null}
              </MarkerClustererF>
            </GoogleMap>
          )}

          {/* Live Violation Beacons Badge */}
          <div className="absolute right-5 top-5 z-30 rounded bg-[#142841]/95 px-4 py-3 text-xs font-bold text-white shadow-lg">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-red-500 shadow-[0_0_14px_rgba(239,68,68,0.9)]" />
              Violation Map
            </div>
          </div>

          {/* Active Violations Box */}
          {/* Warning / Violation Count Box */}
          <div className="absolute bottom-6 right-6 z-30 rounded-lg border border-slate-600 bg-[#203852]/95 px-5 py-4 shadow-lg">
            <p className="text-xs font-bold uppercase text-slate-400">
              Map Event Counts
            </p>

            <div className="mt-3 space-y-2 text-sm font-bold text-white">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.8)]" />
                  <span>Warnings</span>
                </div>

                <span className="text-amber-300">
                  {activeWarningCount.toLocaleString("en-US")}
                </span>
              </div>

              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400 shadow-[0_0_10px_rgba(239,68,68,0.8)]" />
                  <span>Violations</span>
                </div>

                <span className="text-red-300">
                  {activeViolationCount.toLocaleString("en-US")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div
          className="rounded-lg border shadow-xl min-h-[500px]"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div
            className="border-b px-5 py-5"
            style={{ borderColor: colors.border }}
          >
            <h3
              className="text-sm font-extrabold uppercase tracking-wide"
              style={{ color: colors.text }}
            >
              Top Repeat Offenders
            </h3>
          </div>

          <div className="px-5">
            {loadingTopOffenders ? (
              <div
                className="flex min-h-[220px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                Loading offenders...
              </div>
            ) : topOffenders.length === 0 ? (
              <div
                className="flex min-h-[220px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                No repeat offenders found.
              </div>
            ) : (
              topOffenders.map((item, index) => (
                <div
                  key={`${item.meter_id}-${index}`}
                  className="border-b py-4 last:border-b-0"
                  style={{ borderColor: colors.border }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-sm font-semibold"
                        style={{ color: colors.text }}
                        title={item.location_address_line1}
                      >
                        {item.location_address_line1 || "-"}
                      </p>

                      <div
                        className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        <span>Meter: {item.meter_id || "-"}</span>
                        <span>{item.location_city || "-"}</span>
                      </div>

                      <p
                        className="mt-1 text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        Last offense:{" "}
                        {formatOffenseDate(item.last_offense_date)}
                      </p>
                    </div>

                    <p
                      className={`shrink-0 text-sm font-extrabold ${
                        Number(item.offense_count || 0) >= 3
                          ? "text-red-400"
                          : Number(item.offense_count || 0) >= 2
                            ? "text-orange-400"
                            : "text-slate-500"
                      }`}
                    >
                      {Number(item.offense_count || 0)} Hits
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="mt-6 grid grid-cols-1 items-stretch gap-6 xl:grid-cols-[1fr_1fr_250px_250px]">
        {/* Violations by Day */}
        <div
          className="h-full rounded-lg border p-5 shadow-xl"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <h3
            className="text-sm font-extrabold uppercase tracking-wide"
            style={{ color: colors.primary }}
          >
            Violations by Day of Week
          </h3>

          {loadingViolationsByDay ? (
            <div
              className="flex h-[184px] items-center justify-center text-sm font-semibold"
              style={{ color: colors.textSecondary }}
            >
              Loading violations...
            </div>
          ) : violationsByDay.length === 0 ||
            violationsByDay.every((item) => Number(item.count || 0) === 0) ? (
            <div
              className="flex h-[184px] items-center justify-center text-sm font-semibold"
              style={{ color: colors.textSecondary }}
            >
              No violations found.
            </div>
          ) : (
            <div className="relative mt-10 flex h-[184px] items-end justify-between gap-3">
              {dayTooltip && (
                <div
                  className="pointer-events-none absolute z-30 min-w-[130px] rounded-lg border px-3 py-2 text-xs shadow-xl"
                  style={{
                    left: dayTooltip.x,
                    top: dayTooltip.y,
                    transform: "translate(-50%, -100%)",
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  <p className="font-bold" style={{ color: colors.primary }}>
                    {dayTooltip.day}
                  </p>
                  <p className="mt-1" style={{ color: colors.textSecondary }}>
                    Violations:{" "}
                    <span style={{ color: colors.text, fontWeight: 700 }}>
                      {dayTooltip.count}
                    </span>
                  </p>
                </div>
              )}

              {violationsByDay.map((item) => {
                const isPeakDay = Number(item.count || 0) >= 3;

                return (
                  <div
                    key={item.day}
                    className="flex h-full flex-1 flex-col justify-end"
                  >
                    <div className="flex h-full items-end">
                      <div
                        className={`relative w-full cursor-pointer rounded-t-sm transition-all duration-300 hover:opacity-90 ${
                          isPeakDay
                            ? "bg-[#0ea5e9] shadow-[0_0_18px_rgba(14,165,233,0.45)]"
                            : "bg-[#14466f]"
                        }`}
                        style={{ height: `${item.value}%` }}
                        onMouseEnter={(e) => {
                          const parentRect =
                            e.currentTarget.parentElement.parentElement.parentElement.getBoundingClientRect();
                          const barRect =
                            e.currentTarget.getBoundingClientRect();

                          setDayTooltip({
                            day: item.day,
                            count: item.count,
                            x:
                              barRect.left -
                              parentRect.left +
                              barRect.width / 2,
                            y: barRect.top - parentRect.top - 8,
                          });
                        }}
                        onMouseMove={(e) => {
                          const parentRect =
                            e.currentTarget.parentElement.parentElement.parentElement.getBoundingClientRect();
                          const barRect =
                            e.currentTarget.getBoundingClientRect();

                          setDayTooltip({
                            day: item.day,
                            count: item.count,
                            x:
                              barRect.left -
                              parentRect.left +
                              barRect.width / 2,
                            y: barRect.top - parentRect.top - 8,
                          });
                        }}
                        onMouseLeave={() => setDayTooltip(null)}
                      >
                        {Number(item.count || 0) > 0 && (
                          <span
                            className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-bold"
                            style={{
                              color: isPeakDay
                                ? colors.primary
                                : colors.textSecondary,
                            }}
                          >
                            {item.count}
                          </span>
                        )}
                      </div>
                    </div>

                    <span
                      className={`mt-3 text-center text-xs font-bold ${
                        isPeakDay ? "text-sky-300" : "text-slate-500"
                      }`}
                    >
                      {item.day}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Peak Hours */}
        {/* Peak Hours */}
        <div
          className="h-full rounded-lg border p-5 shadow-xl min-h-[280px]"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <h3
            className="text-sm font-extrabold uppercase tracking-wide"
            style={{ color: colors.primary }}
          >
            Violations by Peak Hours
          </h3>

          {loadingViolationsByHour ? (
            <div
              className="flex min-h-[184px] items-center justify-center text-sm font-semibold"
              style={{ color: colors.textSecondary }}
            >
              Loading peak hours...
            </div>
          ) : violationsByHour.length === 0 ||
            violationsByHour.every((item) => Number(item.count || 0) === 0) ? (
            <div
              className="flex min-h-[184px] items-center justify-center text-sm font-semibold"
              style={{ color: colors.textSecondary }}
            >
              No peak hour violations found.
            </div>
          ) : (
            <div className="mt-7 space-y-5">
              {violationsByHour.map((item) => {
                const isPeakHour = Number(item.count || 0) >= 3;

                return (
                  <div key={item.hour}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p
                        className="text-xs font-bold"
                        style={{ color: colors.text }}
                      >
                        {item.label}
                      </p>

                      <p
                        className="text-xs font-bold"
                        style={{
                          color: isPeakHour ? "#0ea5e9" : colors.textSecondary,
                        }}
                      >
                        {item.count} Violations
                      </p>
                    </div>

                    <div
                      className="h-2 overflow-hidden rounded-full"
                      style={{ backgroundColor: `${colors.border}80` }}
                      title={`${item.label}: ${item.count} violations`}
                    >
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          isPeakHour
                            ? "bg-[#0ea5e9] shadow-[0_0_14px_rgba(14,165,233,0.45)]"
                            : "bg-[#14466f]"
                        }`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Rate Code Split */}
        {/* Rate Code Split */}
        <div
          className="h-full rounded-lg border p-5 shadow-xl"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex h-full min-h-[230px] flex-col items-center justify-center">
            <h3
              className="text-xs font-extrabold uppercase tracking-wide"
              style={{ color: colors.primary }}
            >
              Violations Category %
            </h3>

            {loadingRateCodeSplit ? (
              <div
                className="flex min-h-[160px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                Loading rate codes...
              </div>
            ) : rateCodeChartData.length === 0 ? (
              <div
                className="flex min-h-[160px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                No rate code data found.
              </div>
            ) : (
              <>
                <div className="relative mt-5 h-28 w-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={rateCodeChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={34}
                        outerRadius={52}
                        paddingAngle={1}
                        stroke="none"
                        isAnimationActive={false}
                      >
                        {rateCodeChartData.map((entry, index) => (
                          <Cell
                            key={`rate-code-cell-${entry.name}-${index}`}
                            fill={entry.color}
                          />
                        ))}
                      </Pie>

                      <Tooltip
                        content={<RateCodeTooltip />}
                        cursor={false}
                        wrapperStyle={{
                          outline: "none",
                          zIndex: 50,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                    style={{ backgroundColor: colors.cardBg }}
                  >
                    <span
                      className="text-center text-sm font-extrabold leading-tight"
                      style={{ color: colors.text }}
                    >
                      {mainRateCode?.percentage
                        ? `${Number(mainRateCode.percentage).toFixed(0)}%`
                        : "0%"}
                    </span>
                  </div>
                </div>

                <div className="mt-5 space-y-2 text-xs">
                  {rateCodeChartData.map((item, index) => (
                    <div
                      key={`${item.name}-${index}`}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />

                        <span style={{ color: colors.textSecondary }}>
                          {item.name}
                        </span>
                      </div>

                      <span
                        className="font-bold"
                        style={{ color: colors.text }}
                      >
                        {item.percentage.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Irrigation Impact */}
        {/* Irrigation Impact */}
        <div
          className="h-full rounded-lg border p-5 shadow-xl"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex h-full min-h-[230px] flex-col items-center justify-center">
            <h3
              className="text-xs font-extrabold uppercase tracking-wide"
              style={{ color: colors.primary }}
            >
              Violations Water %
            </h3>

            {loadingIrrigationImpact ? (
              <div
                className="flex min-h-[160px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                Loading impact...
              </div>
            ) : Number(irrigationImpact.total_water || 0) === 0 ? (
              <div
                className="flex min-h-[160px] items-center justify-center text-sm font-semibold"
                style={{ color: colors.textSecondary }}
              >
                No irrigation impact data found.
              </div>
            ) : (
              <>
                <div className="relative mt-5 h-28 w-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={irrigationImpactChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={34}
                        outerRadius={52}
                        paddingAngle={1}
                        stroke="none"
                        isAnimationActive={false}
                      >
                        {irrigationImpactChartData.map((entry, index) => (
                          <Cell
                            key={`irrigation-impact-cell-${entry.name}-${index}`}
                            fill={entry.color}
                          />
                        ))}
                      </Pie>

                      <Tooltip
                        content={<IrrigationImpactTooltip />}
                        cursor={false}
                        wrapperStyle={{
                          outline: "none",
                          zIndex: 50,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                    style={{ backgroundColor: colors.cardBg }}
                  >
                    <span
                      className="text-center text-sm font-extrabold leading-tight"
                      style={{ color: colors.text }}
                    >
                      {displayIrrigationImpact}
                    </span>
                  </div>
                </div>

                <p
                  className="mt-4 text-xs font-medium uppercase"
                  style={{ color: colors.textSecondary }}
                >
                  Contribution vs Total
                </p>

                <div className="mt-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between gap-4">
                    <span style={{ color: colors.textSecondary }}>
                      Violation Water
                    </span>
                    <span className="font-bold" style={{ color: colors.text }}>
                      {formatGallons(irrigationImpact.violation_water)} GAL
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <span style={{ color: colors.textSecondary }}>
                      Total Water
                    </span>
                    <span className="font-bold" style={{ color: colors.text }}>
                      {formatGallons(irrigationImpact.total_water)} GAL
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
<DroughtTrendChart
  selectedRange={selectedRange || "1M"}
  getDroughtTrendAnalysis={getDroughtTrendAnalysis}
/>
      {/* Recent Events Log */}
      <div className="mt-6 overflow-hidden rounded-lg border border-slate-700/70 bg-[#162a41] shadow-xl mb-5">
        <div className="flex items-center justify-between border-b border-slate-700 px-6 py-6">
          <h3 className="text-xl font-bold text-white">Recent Events Log</h3>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={downloadRecentEventsCSV}
              disabled={recentEvents.length === 0}
              className="rounded-lg border border-slate-600 bg-[#203852] px-4 py-2 text-sm font-medium text-white hover:bg-[#294766] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Export CSV
            </button>

            <button className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-bold text-white hover:bg-sky-400">
              View All
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-700/70 text-left">
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Address
                </th>
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Meter ID
                </th>
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Event Time
                </th>
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Duration
                </th>
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Avg Flow
                </th>
                <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Type
                </th>
                <th className="px-6 py-5 text-right text-xs font-extrabold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loadingRecentEvents ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-10 text-center text-sm font-semibold text-sky-200/80"
                  >
                    Loading recent events...
                  </td>
                </tr>
              ) : recentEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-10 text-center text-sm font-semibold text-sky-200/80"
                  >
                    No recent events found.
                  </td>
                </tr>
              ) : (
                recentEvents.map((event, index) => (
                  <tr
                    key={event.id || index}
                    className="border-b border-slate-700/40 last:border-b-0 hover:bg-sky-500/5"
                  >
                    <td className="px-6 py-5 text-sm font-semibold text-white">
                      {event.address}
                    </td>

                    <td className="px-6 py-5 text-sm text-sky-200/80">
                      {event.meterId}
                    </td>

                    <td className="px-6 py-5 text-sm text-sky-200/80">
                      {event.eventTime}
                    </td>

                    <td className="px-6 py-5 text-sm text-sky-200/80">
                      {event.duration}
                    </td>

                    <td className="px-6 py-5 text-sm text-sky-200/80">
                      {event.avgFlow}
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`rounded border px-3 py-1 text-[11px] font-extrabold uppercase ${
                          event.type === "Violation"
                            ? "border-red-500/50 bg-red-500/20 text-red-400"
                            : "border-sky-500/50 bg-sky-500/20 text-sky-400"
                        }`}
                      >
                        {event.type}
                      </span>
                    </td>

                    <td className="px-6 py-5 text-right">
                      <button className="text-slate-500 hover:text-sky-400">
                        <Eye size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export default DroughtDashboard;
